import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mic2, Trophy, Users, Calculator, Check, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { SERVICES, COMPANY_INFO } from '../data/companyData';

const ServicesPage = () => {
  const { language } = useLanguage();
  const serviceIcons = { Mic2, Trophy, Users, Calculator };

  return (
    <>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D91616] mb-4 block">
              {language === 'es' ? 'Servicios' : 'Services'}
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-white mb-6">
              {language === 'es' ? 'SOLUCIONES INTEGRALES PARA TU CARRERA' : 'COMPREHENSIVE SOLUTIONS FOR YOUR CAREER'}
            </h1>
            <p className="text-xl text-zinc-400">
              {language === 'es'
                ? 'Ofrecemos representación de 360° que cubre cada aspecto de tu carrera profesional, desde la negociación de contratos hasta la gestión financiera completa.'
                : 'We offer 360° representation covering every aspect of your professional career, from contract negotiation to complete financial management.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      {Object.entries(SERVICES).map(([key, service], index) => {
        const Icon = serviceIcons[service.icon] || Mic2;
        const isEven = index % 2 === 0;
        
        return (
          <section 
            key={key} 
            className={`py-24 ${isEven ? 'bg-[#0A0A0A]' : 'bg-[#050505]'}`}
            id={key}
          >
            <div className="max-w-7xl mx-auto px-6">
              <div className={`grid lg:grid-cols-2 gap-16 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                <motion.div
                  initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className={!isEven ? 'lg:order-2' : ''}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-[#D91616] flex items-center justify-center">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="font-outfit text-3xl font-bold text-white uppercase">{service.title[language]}</h2>
                    </div>
                  </div>
                  
                  <p className="text-zinc-400 text-lg mb-8">{service.description[language]}</p>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.items[language].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 }}
                        className="flex items-start gap-3"
                      >
                        <Check className="w-5 h-5 text-[#D91616] mt-0.5 flex-shrink-0" />
                        <span className="text-zinc-300 text-sm">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: isEven ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className={`relative ${!isEven ? 'lg:order-1' : ''}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={
                        key === 'artists' ? 'https://images.pexels.com/photos/7715781/pexels-photo-7715781.jpeg?auto=compress&w=800' :
                        key === 'athletes' ? 'https://images.pexels.com/photos/5247133/pexels-photo-5247133.jpeg?auto=compress&w=800' :
                        key === 'influencers' ? 'https://images.pexels.com/photos/12432695/pexels-photo-12432695.jpeg?auto=compress&w=800' :
                        'https://images.pexels.com/photos/8353820/pexels-photo-8353820.jpeg?auto=compress&w=800'
                      }
                      alt={service.title[language]}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#D91616]" />
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Process */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D91616] mb-4 block">
              {language === 'es' ? 'Proceso' : 'Process'}
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white">
              {language === 'es' ? 'CÓMO TRABAJAMOS CONTIGO' : 'HOW WE WORK WITH YOU'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { num: '01', title: { es: 'Evaluación', en: 'Evaluation' }, desc: { es: 'Analizamos tu perfil y potencial', en: 'We analyze your profile and potential' } },
              { num: '02', title: { es: 'Estrategia', en: 'Strategy' }, desc: { es: 'Diseñamos un plan personalizado', en: 'We design a personalized plan' } },
              { num: '03', title: { es: 'Ejecución', en: 'Execution' }, desc: { es: 'Implementamos y negociamos', en: 'We implement and negotiate' } },
              { num: '04', title: { es: 'Crecimiento', en: 'Growth' }, desc: { es: 'Escalamos tu carrera', en: 'We scale your career' } }
            ].map((step, index) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-20 h-20 mx-auto mb-6 border-2 border-[#D91616] flex items-center justify-center">
                  <span className="text-2xl font-black text-[#D91616]">{step.num}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title[language]}</h3>
                <p className="text-sm text-zinc-500">{step.desc[language]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#D91616]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-6">
            {language === 'es' ? '¿LISTO PARA TRANSFORMAR TU CARRERA?' : 'READY TO TRANSFORM YOUR CAREER?'}
          </h2>
          <p className="text-white/80 mb-8">
            {language === 'es'
              ? 'Agenda una llamada con nuestro equipo y descubre cómo podemos ayudarte.'
              : 'Schedule a call with our team and discover how we can help you.'}
          </p>
          <Link
            to={language === 'es' ? '/contacto' : '/contact'}
            className="inline-flex items-center gap-3 bg-white text-[#D91616] px-8 py-4 font-bold uppercase tracking-wider hover:bg-zinc-100 transition-all"
          >
            {language === 'es' ? 'Contactar Ahora' : 'Contact Now'}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;
